import { offerlistingStatuses, offerStatus, trucklaneStatusEnum } from '../../enums';
import { OfferCompInventory } from '../../graphql/query-result-types';
import { OfferCardOfferShift } from './offer-card-v3/offer-card-types';

interface ValueTextPair {
  text: string;
  value: string;
}

export interface OfferCompFilters {
  sites: { value: string }[];
  status: ValueTextPair[];
  showTied?: boolean;
  belowReservePrice?: boolean;
  suggestedAward?: boolean;
  postAwardUpdates?: boolean;
  categories: ValueTextPair[];
  businessUnits: ValueTextPair[];
  truckTypes: string[];
  negotiationStatus: string[];
  logistics: string[];
  inventoryTypes: ValueTextPair[];
  codeDate: { start?: Date; end?: Date };
  trucklaneStatus: trucklaneStatusEnum[];
  distributionLists: ValueTextPair[];
  importBatches: ValueTextPair[];
  buyers: ValueTextPair[];
  offerShifts: string[];
}

const shouldIncludeInSiteFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.sites.length === 0 || filters.sites.some((site) => site.value === row.site._id);

const doesOfferStatusMatchFilter = (value: string, row: OfferCompInventory): boolean => {
  const noOffers = value === offerStatus.noOffers && row.offerListingCount === 0;
  const offersAwarded = value === offerStatus.offersAwarded && row.availableQuantity === 0;
  const offersAvailable = value === offerStatus.offersAvailable && row.offerListingCount > 0 && !(row.availableQuantity === 0);
  const partiallyAwarded =
    value === offerStatus.partiallyAwarded && row.offerListingCount > 0 && row.availableQuantity < row.onHandQuantity && row.availableQuantity > 0;
  return noOffers || partiallyAwarded || offersAwarded || offersAvailable;
};

const shouldIncludeInStatusFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.status.length === 0 || filters.status.some((status) => doesOfferStatusMatchFilter(status.value, row));

const shouldIncludeInCategoryFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.categories.length === 0 ||
  filters.categories.some((category) => {
    return category.value.toLowerCase() === row.foodType.toLowerCase() || category.value.toLowerCase() === row.foodSubtype.toLowerCase();
  });

const shouldIncludeInTruckTypeFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.truckTypes.length === 0 ||
  filters.truckTypes.some((truckType) => {
    return truckType.toLowerCase() === row.truckType.toLowerCase();
  });

const shouldIncludeInNegotiationStatusFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.negotiationStatus.length === 0 ||
  filters.negotiationStatus.some((negotiationStatusType) => {
    return row.offerListings?.some((offerListing) => offerListing.negotiation?.status?.toLowerCase() === negotiationStatusType.toLowerCase());
  });

const shouldIncludeInLogisticsFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.logistics.length === 0 ||
  filters.logistics.some((logisticsTerm) => {
    return row.offerListings?.some((offerListing) => offerListing.logisticsTerm?.toLowerCase() === logisticsTerm.toLowerCase());
  });

const shouldIncludeInTrucklaneStatusFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean => {
  return (
    !filters.trucklaneStatus.length ||
    (filters.trucklaneStatus.includes(trucklaneStatusEnum.active) &&
      row.offerListings?.some((offerListing) => [offerlistingStatuses.ACCEPTED, offerlistingStatuses.AWARDED].includes(offerListing.status))) ||
    (filters.trucklaneStatus.includes(trucklaneStatusEnum.inactive) &&
      row.offerListings?.every((offerListing) => ![offerlistingStatuses.ACCEPTED, offerlistingStatuses.AWARDED].includes(offerListing.status)))
  );
};

const shouldIncludeInBusinessUnitFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.businessUnits.length === 0 ||
  filters.businessUnits.some((businessUnit) => {
    return businessUnit.value.toLowerCase() === row.businessUnit.toLowerCase();
  });

const shouldIncludeInTiedFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.showTied === true ? Array.isArray(row.tags) && row.tags.includes('tied') : true;

const shouldIncludeInPostAwardUpdatesFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.postAwardUpdates === true
    ? row.offerListings?.some(
        (offerListing) =>
          offerListing.postAwardUpdates && offerListing.postAwardUpdates.status === 'PENDING' && offerListing.status === offerlistingStatuses.AWARDED,
      )
    : true;

const shouldIncludeInSuggestedAwardFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.suggestedAward === true
    ? row.offerListings?.some((offerListing) => offerListing.suggestions?.award?.suggested && offerListing.status !== offerlistingStatuses.AWARDED)
    : true;

const shouldIncludeInReservePriceFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean => {
  return filters.belowReservePrice === true
    ? Array.isArray(row.offerListings) && row.offerListings?.some((offerListing) => offerListing?.tags?.includes('below-reserve'))
    : true;
};

const shouldIncludeInInventoryTypeFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean =>
  filters.inventoryTypes.length === 0 ||
  filters.inventoryTypes.some((inventoryType) => {
    return inventoryType.value.toLowerCase() === row.inventoryType.toLowerCase();
  });

const shouldIncludeInCodeDateFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean => {
  if (filters.codeDate.start !== undefined && filters.codeDate.end !== undefined) {
    return (
      new Date(row.bestByDate).toISOString() >= new Date(filters.codeDate.start).toISOString() &&
      new Date(row.bestByDate).toISOString() <= new Date(filters.codeDate.end).toISOString()
    );
  }
  return true;
};

const shouldIncludeInDistributionListFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean => {
  return (
    filters.distributionLists.length === 0 ||
    filters.distributionLists.some((distributionList) => {
      return row.distributionLists?.some((dl) => {
        return dl.listId === distributionList.value;
      });
    })
  );
};

const shouldIncludeInImportBatchFilter = (row: OfferCompInventory, filters: OfferCompFilters): boolean => {
  return filters.importBatches.length === 0 || filters.importBatches.map((b) => b.value).includes(row.importBatch);
};

const shouldIncludeInBuyerFilter = (row: OfferCompInventory, filters: OfferCompFilters) => {
  return (
    filters.buyers.length === 0 ||
    filters.buyers.some((buyer) => {
      return row.buyerSiteNames.find((name) => name.toLowerCase() === buyer.value.toLowerCase());
    })
  );
};

const shouldIncludeInShiftsFilter = (row: OfferCompInventory, filters: OfferCompFilters, shiftMap: Map<string, OfferCardOfferShift[]>): boolean => {
  if (filters.offerShifts.length === 0) return true;
  if (filters.offerShifts[0] === 'show') {
    if (shiftMap.has(row._id)) return true;
    if (row.offerListings.some((ol) => shiftMap.has(ol._id))) return true;
  } else {
    return true;
  }
  return false;
};

export const doesInventoryRowMatchFilters = (
  row: OfferCompInventory,
  filters: OfferCompFilters,
  shiftMap: Map<string, OfferCardOfferShift[]> | undefined,
): boolean =>
  shouldIncludeInSiteFilter(row, filters) &&
  shouldIncludeInStatusFilter(row, filters) &&
  shouldIncludeInCategoryFilter(row, filters) &&
  shouldIncludeInBusinessUnitFilter(row, filters) &&
  shouldIncludeInTiedFilter(row, filters) &&
  shouldIncludeInReservePriceFilter(row, filters) &&
  shouldIncludeInSuggestedAwardFilter(row, filters) &&
  shouldIncludeInPostAwardUpdatesFilter(row, filters) &&
  shouldIncludeInTruckTypeFilter(row, filters) &&
  shouldIncludeInLogisticsFilter(row, filters) &&
  shouldIncludeInNegotiationStatusFilter(row, filters) &&
  shouldIncludeInInventoryTypeFilter(row, filters) &&
  shouldIncludeInCodeDateFilter(row, filters) &&
  shouldIncludeInTrucklaneStatusFilter(row, filters) &&
  shouldIncludeInDistributionListFilter(row, filters) &&
  shouldIncludeInImportBatchFilter(row, filters) &&
  shouldIncludeInBuyerFilter(row, filters) &&
  (shiftMap ? shouldIncludeInShiftsFilter(row, filters, shiftMap) : true) &&
  row.onHandQuantity > 0;
